import { useCallback, useEffect, useMemo, useState } from "react";
import { TextField, Autocomplete } from "@mui/material";
import debounce from "lodash/debounce";
import { EntreprisesServices } from "$services";
import {
  InseeApiCompany,
  InseeApiResponse,
} from "$services/Entreprises.service";
import JSelectDisplayOption from "$components/JSelectDisplayOption/JSelectDisplayOption";

type Props = {
  value: any;
  onChange: (values: any, rows: any) => void;
  isAgency: boolean;
};

function CompanySiretSelect({ value, onChange, isAgency }: Props) {
  const [companyOptions, setCompanyOptions] = useState([]);
  const [filter, setFilter] = useState("");

  const handleSearchInput = useMemo(
    () =>
      debounce((val) => {
        setFilter(val);
      }, 300), // Reduced debounce time for better UX
    []
  );

  const getSuggestions = useCallback(async (val: string) => {
    try {
      const res = await EntreprisesServices.GetCompanyNameData(val);
      return res ?? [];
    } catch (e) {
      console.error("Error fetching company suggestions:", e);
      return [];
    }
  }, []);

  const processCompanyOptions = useCallback((response: InseeApiResponse) => {
    if (!response?.etablissements) return [];

    return response.etablissements.map((etablissement: InseeApiCompany) => {
      const companyName = etablissement?.uniteLegale?.denominationUniteLegale;
      const adresseEtablissement = etablissement?.adresseEtablissement;
      const {
        codePostalEtablissement: postalCode,
        libelleCommuneEtablissement: city,
        libelleVoieEtablissement: streetName,
        numeroVoieEtablissement: houseNumber,
        typeVoieEtablissement,
      } = adresseEtablissement || {};

      const streetType = typeVoieEtablissement;
      const address = [houseNumber, streetType, streetName]
        .filter(Boolean)
        .join(" ");

      return {
        label: companyName,
        address,
        postalCode,
        city,
        siret: etablissement?.siret,
      };
    });
  }, []);

  useEffect(() => {
    if (!filter) {
      setCompanyOptions([]);
      return;
    }

    let isCancelled = false;

    getSuggestions(filter).then((response: InseeApiResponse) => {
      if (!isCancelled) {
        const options = processCompanyOptions(response);
        setCompanyOptions(options);
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [filter, getSuggestions, processCompanyOptions]);

  const memoizedRenderOption = useCallback(
    (props: any, option: any) => (
      <div {...props}>
        <JSelectDisplayOption
          value1={option.label}
          value2={`${option.address} - ${option.postalCode} ${option.city}`}
        />
      </div>
    ),
    []
  );

  const handleInputChange = useCallback(
    (e: any, val: string, reason: string) => {
      if (reason === "input") {
        handleSearchInput(val);
        onChange(val, val);
      }
    },
    [handleSearchInput, onChange]
  );

  const handleChange = useCallback(
    (event: any, newValues: any) => {
      onChange(newValues, newValues);
    },
    [onChange]
  );

  const getOptionLabel = useCallback(
    (option: any) => option?.label || option,
    []
  );

  return (
    <Autocomplete
      onInputChange={handleInputChange}
      disableClearable
      options={companyOptions}
      onChange={handleChange}
      value={value}
      getOptionLabel={getOptionLabel}
      renderOption={memoizedRenderOption}
      noOptionsText="Saisir une entreprise"
      renderInput={(params: any) => (
        <TextField
          {...params}
          label={isAgency ? "Nom de l'agence transporteur *" : "Nom *"}
          variant="outlined"
          style={{ width: "100%" }}
        />
      )}
    />
  );
}

export default CompanySiretSelect;
